import React, { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import useTranslator from "@pma/hooks/use-translator"
import { ChevronDown, ChevronUp, LoaderCircle, GripVertical } from "lucide-react"
import { DictType } from "@pma/types"
import {  formatStringWithLink } from "@pma/utils/helpers"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Button } from "@pma/components/common/button"
import TaskStatusEditor from "../task-status-editor"
import TaskPriorityEditor from "../task-priority-editor"
import AssignToUser from "../assign-to-user"
import PinTask from "../pin-task"
import ExpandTask from "../expand-task"
import DeleteTask from "../delete-task"
import { GroupedTasksContext } from "../grouped-tasks/context/GroupedTasksProvider"
import { IGroupSubTaskInput } from "../grouped-tasks/api-fns"
import CardEditor from "./card-editor"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

interface Props {
  task: DictType
  parentTaskId: number
  index?: number
  group_id?: number | null 
}

function SubTaskCard({ task, parentTaskId, index, group_id }: Props) {
  const { translate, locale } = useTranslator()
  const { fetchSubTaskMutation, setTaskExpandForLoading } = React.useContext(GroupedTasksContext)
  const [isExpanded, setExpanded] = useState(false)

  
  const sortableId = parentTaskId && task.id 
    ? `subtask_${parentTaskId}_${task.id}_${group_id ?? -1}` 
    : `subtask_${task.id}_${group_id ?? -1}`

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: {
      type: "Task",
      task,
      isSubtask: true,
      parentTaskId,
      groupId: group_id ?? -1,
    },
    disabled: !parentTaskId || !task.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : "auto",
  }

  const handleSubTaskFetching = () => {
    if (isExpanded) {
      setExpanded(false)
    } else {
      setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
      setExpanded(true)
    }
  }

  const loading = fetchSubTaskMutation?.isPending
  const isSuccess = fetchSubTaskMutation?.isSuccess

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className='h-full w-full hover:ring-2 hover:ring-inset hover:ring-primary/40'>
        <CardHeader className='flex flex-row items-center justify-between px-5 py-3'>
          <div className='flex w-[230px] flex-row gap-5'>
            <div>
              <p className='text-xs font-semibold'>{translate(`Ticket`)}</p>
              <p className='text-xs text-slate-500'>{`${translate("PR")}-${task.id}`}</p>
            </div>
            <div>
              <p className='text-xs font-semibold'>{translate("Title")}</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className='max-w-[18ch] truncate text-ellipsis text-xs text-slate-500'>
                    {task?.title}
                  </p>
                </TooltipTrigger>
                <TooltipContent side='bottom' className='flex items-center gap-4'>
                  <p className='truncate text-ellipsis text-xs text-slate-500'>{task?.title}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            {/* CHANGED: Increased hit area for drag handle */}
            {parentTaskId && task.id && (
              <div
                {...listeners}
                className='cursor-grab touch-none select-none p-2'
                style={{ touchAction: "none" }}
              >
                <GripVertical className='text-gray-500' size={20} />
              </div>
            )}
            {/* <CardTitle>{`Ticket PR-${task.id} Title ${<p className='text-xs text-slate-500 max-w-[42ch] truncate text-ellipsis'>{task?.title}</p> }`}</CardTitle> */}
            {/* {loading ? (
          <LoaderCircle className={cn("animate-spin text-primary")} size={24} />
        ) : ( */}
            <Button
              size='lg'
              className='m-0 h-6 w-6 rounded-full p-0'
              style={{ margin: 0 }}
              onClick={handleSubTaskFetching}
              disabled={loading}
            >
              {loading ? (
                <LoaderCircle className='animate-spin' size={16} />
              ) : isExpanded ? (
                <ChevronUp />
              ) : (
                <ChevronDown />
              )}
            </Button>
            {/* )} */}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className='flex flex-col gap-3 p-2 text-sm'>
            <div className='w-full'>
              {/* <div className='flex h-9 w-full flex-row items-center justify-between py-2'>
              <span className='text-xs text-slate-500'>{translate("Created")}</span>
              <span className='text-xs font-semibold'>
                {isHebrew ? formatDate_he(task?.created_at) : formatDate(task?.created_at)}
              </span>
            </div> */}
              {/* <Separator /> */}
              <div className='flex flex-col gap-3 overflow-hidden p-2'>
                <div className='align-center flex flex-row gap-2'>
                  <span className='flex items-center text-xs text-slate-500'>
                    {translate("Title")}:
                  </span>
                  <CardEditor id={task?.id} title='title' value={task?.title} parentTaskId={parentTaskId}>
                    <div
                      className='max-w-[22ch] truncate text-ellipsis text-left text-sm md:w-full'
                      dangerouslySetInnerHTML={{
                        __html: formatStringWithLink(task.title),
                      }}
                    />
                  </CardEditor>
                </div>
                <div className='align-center flex flex-row gap-2'>
                  <span className='flex items-center text-xs text-slate-500'>
                    {translate("Description")}:
                  </span>
                  <CardEditor id={task?.id} title='description' value={task?.description} parentTaskId={parentTaskId}>
                    {task?.description ? (
                      <div
                        className='max-w-[18ch] truncate text-ellipsis text-left text-xs text-slate-500 md:w-full'
                        dangerouslySetInnerHTML={{
                          __html: formatStringWithLink(task?.description),
                        }}
                      />
                    ) : (
                      "---"
                    )}
                  </CardEditor>
                </div>
                <div className='flex w-full items-center justify-between gap-2'>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='truncate text-ellipsis text-xs text-slate-500'>
                      {translate("Status")}:
                    </span>
                    <TaskStatusEditor status={task?.status} taskId={task?.id} parentTaskId={parentTaskId} />
                  </div>
                </div>
                <div className='flex w-full items-center justify-between gap-2'>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='truncate text-ellipsis text-xs text-slate-500'>
                      {translate("Priority")}:
                    </span>
                    <TaskPriorityEditor priority={task?._priority} taskId={task?.id} parentTaskId={parentTaskId} />
                  </div>
                </div>
                <div className='flex w-full items-center justify-between'>
                  {task?.responsible_id && (
                    <div className='flex flex-1 gap-2'>
                      <span className='truncate text-ellipsis text-xs text-slate-500'>
                        {translate("Assign users")}:
                      </span>
                      <AssignToUser taskId={task?.id} responsible={task?.responsible_id || []} parentTaskId={parentTaskId} />
                    </div>
                  )}
                </div>
                <div className='flex w-full items-center justify-between gap-0.5'>
                  <small className='text-xs text-slate-500'>{`${translate("PR")}-${task.id}`}</small>
                  <div className='flex flex-1 items-center justify-end'>
                    <PinTask task={task} />
                    <ExpandTask task={task} />
                    {/* <Tooltip>
                    <TooltipTrigger>
                      <Button variant={"link"} className='h-6 w-6' onClick={openTask} size={"icon"}>
                        <Eye size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{translate("Go to task")}</TooltipContent>
                  </Tooltip> */}
                    <DeleteTask task={task} />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}

export default SubTaskCard
